import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Chip, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import LocalAtmRoundedIcon from '@mui/icons-material/LocalAtmRounded';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { PlannerSummary } from '../../types/planner';
import { alpha } from '@mui/material/styles';
import { MarkdownText } from '../common/MarkdownText';
import { emphasizeMarkdownLead } from '../../utils/markdown';


type DestinationSnapshot = {
  name?: string;
  city?: string;
  description: string;
  country: string;
  climate: string;
  language: string;
  currency: string;
} | null;

interface TripOverviewHeroProps {
  summary: PlannerSummary | null;
  destinationInfo?: DestinationSnapshot;
  dayCount?: number;
  budget?: string;
  actions?: React.ReactNode;
  destinationImageUrls?: string[];
}

const renderChipLabel = (value: string) => (
  <MarkdownText
    component="span"
    variant="body2"
    sx={{ display: 'inline', lineHeight: 1.3 }}
  >
    {emphasizeMarkdownLead(value)}
  </MarkdownText>
);

const mapToChipData = (
  summary: PlannerSummary | null,
  destinationInfo: DestinationSnapshot,
  dayCount?: number,
  budget?: string,
) => {
  const chips: Array<{ icon: React.ReactNode; label: React.ReactNode; key: string }> = [];

  // Handle multi-city destination dates first
  if (summary?.profile?.destinationDates && summary.profile.destinationDates.length > 0) {
    summary.profile.destinationDates.forEach((destDate) => {
      const label = `${destDate.destination}: ${destDate.durationLabel}`;
      chips.push({
        icon: <AccessTimeRoundedIcon sx={{ color: 'secondary.main' }} />,
        label: renderChipLabel(label),
        key: `duration-${destDate.destination}-${destDate.durationLabel}`,
      });
    });
  } else if (summary?.profile?.durationLabel) {
    chips.push({ icon: <AccessTimeRoundedIcon sx={{ color: 'secondary.main' }} />, label: renderChipLabel(summary.profile.durationLabel), key: `duration-${summary.profile.durationLabel}` });
  } else if (dayCount) {
    const text = `${dayCount} day${dayCount > 1 ? 's' : ''}`;
    chips.push({ icon: <AccessTimeRoundedIcon sx={{ color: 'secondary.main' }} />, label: renderChipLabel(text), key: `duration-${text}` });
  }

  if (destinationInfo?.country) {
    chips.push({ icon: <PlaceRoundedIcon sx={{ color: 'secondary.main' }} />, label: renderChipLabel(destinationInfo.country), key: `country-${destinationInfo.country}` });
  }

  if (budget) {
    const text = `Budget • ${budget}`;
    chips.push({ icon: <LocalAtmRoundedIcon sx={{ color: 'secondary.main' }} />, label: renderChipLabel(text), key: `budget-${budget}` });
  }

  if (destinationInfo?.language) {
    chips.push({ icon: <LanguageRoundedIcon sx={{ color: 'secondary.main' }} />, label: renderChipLabel(destinationInfo.language), key: `language-${destinationInfo.language}` });
  }

  if (destinationInfo?.climate) {
    chips.push({ icon: <WbSunnyRoundedIcon sx={{ color: 'secondary.main' }} />, label: renderChipLabel(destinationInfo.climate), key: `climate-${destinationInfo.climate}` });
  }

  return chips;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _normalizeUrl = (value?: string | null): string => {
  if (!value) {
    return '';
  }
  return value.trim();
};

export const TripOverviewHero: React.FC<TripOverviewHeroProps> = ({
  summary,
  destinationInfo,
  dayCount,
  budget,
  actions,
  destinationImageUrls,
}) => {
  const navigate = useNavigate();
  const { location } = useParams<{ location?: string }>();
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [isImageLoaded, setIsImageLoaded] = React.useState(false);
  const preloadedImagesRef = React.useRef<Set<string>>(new Set());
  const loadedImagesRef = React.useRef<Set<string>>(new Set());
  const imagesCount = destinationImageUrls?.length ?? 0;

  // Always show the first image when the destination changes
  React.useEffect(() => {
    if (summary?.destination) {
      setCurrentImageIndex(0);
      preloadedImagesRef.current = new Set();
      loadedImagesRef.current = new Set();
      setIsImageLoaded(false);
    }
  }, [summary?.destination]);

  React.useEffect(() => {
    setCurrentImageIndex(0);
    preloadedImagesRef.current = new Set();
    loadedImagesRef.current = new Set();
    setIsImageLoaded(false);
  }, [destinationImageUrls]);

  // Clamp the carousel index when the available image set changes
  React.useEffect(() => {
    if (imagesCount === 0) {
      setCurrentImageIndex(0);
      return;
    }

    setCurrentImageIndex((prev) => (prev >= imagesCount ? 0 : prev));
  }, [imagesCount]);

  const currentImageUrl =
    imagesCount > 0 ? destinationImageUrls?.[Math.min(currentImageIndex, imagesCount - 1)] : undefined;
  const hasMultipleImages = imagesCount > 1;

  const markImageLoaded = React.useCallback(
    (url?: string) => {
      if (!url) {
        return;
      }
      loadedImagesRef.current.add(url);
      if (url === currentImageUrl) {
        setIsImageLoaded(true);
      }
    },
    [currentImageUrl],
  );

  // Reset loading indicator when the displayed image changes, but skip if already cached
  React.useEffect(() => {
    if (!currentImageUrl) {
      setIsImageLoaded(false);
      return;
    }

    if (loadedImagesRef.current.has(currentImageUrl)) {
      setIsImageLoaded(true);
    } else {
      setIsImageLoaded(false);
    }
  }, [currentImageUrl]);

  // Preload destination hero images for smooth carousel transitions
  React.useEffect(() => {
    if (!destinationImageUrls?.length) {
      return;
    }

    destinationImageUrls.forEach((url) => {
      const trimmed = url?.trim();
      if (!trimmed || preloadedImagesRef.current.has(trimmed)) {
        return;
      }

      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.src = trimmed;
      img.onload = () => markImageLoaded(trimmed);
      img.onerror = () => markImageLoaded(trimmed);
      preloadedImagesRef.current.add(trimmed);
    });
  }, [destinationImageUrls, markImageLoaded]);

  if (!summary) {
    return null;
  }

  const chips = mapToChipData(summary, destinationInfo ?? null, dayCount, budget);

  const handleBackClick = () => {
    // Navigate back to planner input form
    navigate('/');
  };

  const handlePrevImage = () => {
    if (imagesCount === 0) {
      return;
    }
    setCurrentImageIndex((prev) => (prev === 0 ? imagesCount - 1 : prev - 1));
  };

  const handleNextImage = () => {
    if (imagesCount === 0) {
      return;
    }
    setCurrentImageIndex((prev) => (prev === imagesCount - 1 ? 0 : prev + 1));
  };

  return (
    <Box>
      {/* Destination Hero Image Carousel */}
      {currentImageUrl && (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: { xs: 280, sm: 350, md: 400 },
            borderRadius: 4,
            overflow: 'hidden',
            mb: 4,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          }}
        >
          {/* Background Image */}
          <Box
            component="img"
            src={currentImageUrl}
            alt={summary.destination}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            onLoad={() => markImageLoaded(currentImageUrl)}
            onError={() => markImageLoaded(currentImageUrl)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              opacity: isImageLoaded ? 1 : 0,
              transition: 'opacity 0.35s ease',
            }}
          />

          {!isImageLoaded && (
            <Skeleton
              variant="rectangular"
              animation="wave"
              sx={{
                position: 'absolute',
                inset: 0,
                height: '100%',
                width: '100%',
                borderRadius: 'inherit',
                bgcolor: 'rgba(255, 255, 255, 0.08)',
              }}
            />
          )}

          {/* Gradient Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.5) 100%)',
            }}
          />

          {/* Top Navigation Buttons */}
          {location && (
            <IconButton
              onClick={handleBackClick}
              sx={{
                position: 'absolute',
                top: { xs: 20, sm: 24 },
                left: { xs: 20, sm: 24 },
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 56, 92, 0.95)',
                  color: 'white',
                  boxShadow: '0 8px 16px rgba(255, 56, 92, 0.3)',
                },
                transition: 'all 0.2s ease',
                zIndex: 2,
              }}
            >
              <ArrowBackRoundedIcon />
            </IconButton>
          )}
          {actions && (
            <Box
              sx={{
                position: 'absolute',
                top: { xs: 20, sm: 24 },
                right: { xs: 20, sm: 24 },
                zIndex: 2,
                '& .MuiButton-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.75)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  color: 'primary.main',
                  borderColor: 'transparent',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 56, 92, 0.95)',
                    color: 'white',
                    borderColor: 'transparent',
                    boxShadow: '0 8px 16px rgba(255, 56, 92, 0.3)',
                  },
                  transition: 'all 0.2s ease',
                },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                {actions}
              </Stack>
            </Box>
          )}

          {/* Carousel Navigation Buttons */}
          {hasMultipleImages && (
            <>
              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: 'absolute',
                  left: { xs: 16, sm: 24 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                  },
                  transition: 'all 0.2s ease',
                  zIndex: 2,
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                }}
              >
                <ArrowBackRoundedIcon />
              </IconButton>
              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: 'absolute',
                  right: { xs: 16, sm: 24 },
                  top: '50%',
                  transform: 'translateY(-50%) rotate(180deg)',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                  },
                  transition: 'all 0.2s ease',
                  zIndex: 2,
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                }}
              >
                <ArrowBackRoundedIcon />
              </IconButton>

              {/* Carousel Indicators */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: { xs: 80, sm: 90, md: 100 },
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 1,
                  zIndex: 2,
                }}
              >
                {destinationImageUrls?.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: index === currentImageIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'white',
                        transform: 'scale(1.2)',
                      },
                    }}
                  />
                ))}
              </Box>
            </>
          )}

          {/* Destination Name Overlay */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: { xs: 3, sm: 4 },
              color: 'white',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src="/favicon.svg"
                alt="TripGen AI Logo"
                sx={{ 
                  width: { xs: '1.5rem', sm: '2rem' },
                  height: { xs: '1.5rem', sm: '2rem' },
                  flexShrink: 0,
                  display: 'block'
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  mb: 0,
                }}
              >
                {(() => {
                  // For multi-city trips, if destination info shows city === country, just show country name
                  if (destinationInfo && destinationInfo.city === destinationInfo.country && summary.destination) {
                    // Check if summary.destination already contains both city and country (e.g., "France, France")
                    const parts = summary.destination.split(',').map(p => p.trim());
                    if (parts.length === 2 && parts[0] === parts[1]) {
                      return parts[0]; // Just return the country name once
                    }
                    // If destination matches the country, just show it once
                    if (summary.destination.toLowerCase() === destinationInfo.country.toLowerCase()) {
                      return destinationInfo.country;
                    }
                  }
                  return summary.destination;
                })()}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Traditional Header (if no image) */}
      {!currentImageUrl && (
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Box sx={{ width: '100%' }}>
            {location ? (
              <Stack direction="row" alignItems="center" spacing={2}>
                <IconButton
                  onClick={handleBackClick}
                  sx={{
                    backgroundColor: 'background.paper',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      boxShadow: '0 8px 16px rgba(255, 56, 92, 0.3)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ArrowBackRoundedIcon />
                </IconButton>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    component="img"
                    src="/favicon.svg"
                    alt="TripGen AI Logo"
                    sx={{ 
                      width: { xs: '1.5rem', sm: '2rem' },
                      height: { xs: '1.5rem', sm: '2rem' },
                      flexShrink: 0,
                      display: 'block'
                    }}
                  />
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: 'white',
                    }}
                  >
                    {(() => {
                      // For multi-city trips, if destination info shows city === country, just show country name
                      if (destinationInfo && destinationInfo.city === destinationInfo.country && summary.destination) {
                        // Check if summary.destination already contains both city and country (e.g., "France, France")
                        const parts = summary.destination.split(',').map(p => p.trim());
                        if (parts.length === 2 && parts[0] === parts[1]) {
                          return parts[0]; // Just return the country name once
                        }
                        // If destination matches the country, just show it once
                        if (summary.destination.toLowerCase() === destinationInfo.country.toLowerCase()) {
                          return destinationInfo.country;
                        }
                      }
                      return summary.destination;
                    })()}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  component="img"
                  src="/favicon.svg"
                  alt="TripGen AI Logo"
                  sx={{ 
                    width: { xs: '1.5rem', sm: '2rem' },
                    height: { xs: '1.5rem', sm: '2rem' },
                    flexShrink: 0,
                    display: 'block'
                  }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: (theme) => theme.palette.primary.main,
                  }}
                >
                  {(() => {
                    // For multi-city trips, if destination info shows city === country, just show country name
                    if (destinationInfo && destinationInfo.city === destinationInfo.country && summary.destination) {
                      // Check if summary.destination already contains both city and country (e.g., "France, France")
                      const parts = summary.destination.split(',').map(p => p.trim());
                      if (parts.length === 2 && parts[0] === parts[1]) {
                        return parts[0]; // Just return the country name once
                      }
                      // If destination matches the country, just show it once
                      if (summary.destination.toLowerCase() === destinationInfo.country.toLowerCase()) {
                        return destinationInfo.country;
                      }
                    }
                    return summary.destination;
                  })()}
                </Typography>
              </Box>
            )}
          </Box>
          {actions && (
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
                {actions}
              </Box>
            </Stack>
          )}
        </Stack>
      )}

      <Stack direction="row" sx={{ mt: 2, flexWrap: 'wrap', gap: 1.5 }}>
        {chips.map((chip) => (
          <Chip
            key={chip.key}
            label={chip.label}
            icon={chip.icon as any}
            variant="outlined"
            sx={(theme) => ({
              backgroundColor: alpha(theme.palette.text.primary, 0.04),
              borderColor: alpha(theme.palette.text.primary, 0.12),
              color: 'text.primary',
              fontWeight: 600,
              borderRadius: 2,
              px: 1,
              height: 'auto',
              '& .MuiChip-label': {
                whiteSpace: 'normal',
                lineHeight: 1.5,
              },
              '& .MuiChip-icon': {
                color: theme.palette.secondary.main,
              },
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.primary, 0.08),
              },
            })}
          />
        ))}
      </Stack>
      {destinationInfo?.description && (
        <MarkdownText variant="body1" sx={{ color: 'text.secondary', mt: 4, maxWidth: 720 }}>
          {destinationInfo.description}
        </MarkdownText>
      )}
    </Box>
  );
};
